import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { extractColors } from 'extract-colors';
import * as Prism from 'prismjs';
import 'prismjs/components/prism-css';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { addColorSwatches } from '../../../../util/code-swatches';
import { BLOG } from '../../../../data/blog/blog.data';
import { PALETTE_ABOUT } from 'src/app/data/tool/tool-about.data';
import { HEX_CODES } from '../../../../data/color/color.data';
import { PaletteSubtitle } from '../../../../component/palette-widget/palette-widget.component';
import { PALETTE_SCHEME_MINS, PaletteGenerationService } from '../../../../service/palette/palette-generation.service';
import { URLS } from '../../../../data/navigation/navigation.data';
import { THEMES, TOOLS } from '../../../../data/product/product.data';
import { Product } from '../../../../model/product/product.model';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { ColorService } from '../../../../service/color/color.service';
import { CSS_SYNTAX_DEFAULT, CSS_SYNTAX_PREMIUM, CssSyntaxTheme } from '../../../../data/theme/css-syntax.data';
import { ProductThemeSelection, ThemeService } from '../../../../service/theme/theme.service';
import { ProductService } from '../../../../service/product/product.service';
import { SeoService } from '../../../../service/seo/seo.service';
import {Clipboard} from "@angular/cdk/clipboard";

@Component( {
  selector: 'app-palette-page',
  templateUrl: './palette-page.component.html',
  styleUrls: [ './palette-page.component.scss', '../tools-page/tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class PalettePageComponent implements OnInit, AfterViewInit, OnDestroy {

  URLS = URLS;
  TOOLS = TOOLS;

  palette = [];

  /** the conversion groups in two balanced columns of whole groups, as the color tool's (owner 2026-10-12): the
   *  left column takes leading groups up to half the rows, each column is one table, so no gap between groups */
  private convColsFor?: unknown;
  private convCols: { title: string; rows: any[] }[][] = [];

  get paletteConversionCols(): { title: string; rows: any[] }[][] {
    const groups = this.paletteConversionGroups || [];
    if ( this.convColsFor === groups ) { return this.convCols; }
    this.convColsFor = groups;
    const weight = ( g: { rows: any[] } ) => g.rows.length + 1;
    const total = groups.reduce( ( n, g ) => n + weight( g ), 0 );
    let acc = 0;
    let splitAt = groups.length;
    for ( let i = 0; i < groups.length; i++ ) {
      acc += weight( groups[ i ] );
      if ( acc >= total / 2 ) { splitAt = i + 1; break; }
    }
    this.convCols = [ groups.slice( 0, splitAt ), groups.slice( splitAt ) ];
    return this.convCols;
  }

  /** the page's top band starts from the palette's most vivid colour (owner 2026-10-12; page-band.scss) */
  @HostBinding( 'style.--band' ) get band(): string {
    const vivid = this.mostVivid( this.pickHeroColors() );
    return `color-mix(in oklab, ${ vivid || 'var(--theme-color)' } 55%, var(--background-color))`;
  }

  /** the band as every colour of the palette side by side, each a little (owner 2026-10-12), fading down to the
   *  page in the stylesheet */
  @HostBinding( 'style.--band-row' ) get bandRow(): string {
    const colors = this.pickHeroColors().filter( c => /^#?[0-9a-f]{6}$/i.test( ( c || '' ).replace( '#', '' ) ) );
    if ( colors.length < 2 ) { return `linear-gradient(180deg, ${ this.band } 0%, var(--background-color) 100%)`; }
    const stops = colors.map( c => `color-mix(in oklab, ${ c.startsWith( '#' ) ? c : '#' + c } 55%, var(--background-color))` );
    return `linear-gradient(90deg, ${ stops.join( ', ' ) })`;
  }

  /** the colour with the most chroma (saturation away from black and white), so a pale first colour leaves no white band */
  private mostVivid( colors: string[] ): string | undefined {
    let best: string | undefined;
    let bestChroma = -1;
    for ( const color of colors ) {
      const hex = ( color || '' ).replace( '#', '' );
      if ( !/^[0-9a-f]{6}$/i.test( hex ) ) { continue; }
      const channels = [ 0, 2, 4 ].map( i => parseInt( hex.substr( i, 2 ), 16 ) / 255 );
      const chroma = Math.max( ...channels ) - Math.min( ...channels );
      if ( chroma > bestChroma ) { bestChroma = chroma; best = '#' + hex; }
    }
    return best;
  }
  // Number of colours the next Initialize / From image action produces (3-10).
  count = 10;
  // The Range field mirrors the generated/loaded colour count, but only on the
  // first load — once set, user edits own it.
  private countSynced = false;

  // "On This Page" sidebar sections (the hero/palette is the implicit "top").
  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'channels', label: 'Color Channels' },
    { key: 'conversions', label: 'Conversions' },
    { key: 'harmony', label: 'Harmony' },
    { key: 'shades', label: 'Tints & Shades' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'vision', label: 'Color Vision' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'themes', label: 'Related Themes' },
    { key: 'blog', label: 'From The Blog' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'palette-generator';
  activeSection = 'top';
  private sectionObserver?: IntersectionObserver;

  // Matching IDE themes whose palette sits closest to this palette.
  relatedThemeList: { product: Product; category: string }[] = [];

  // Aggregate channel stats for the whole palette (RGB weight + average HSL /
  // HSV / CMYK).
  channelStats: { rgb: any[]; hsl: any[]; hsv: any[]; cmyk: any[] } | null = null;

  // Preview section: five full interface blocks, each a different palette role
  // assignment (so more of the palette is used), ranked best to worst by WCAG
  // contrast. Every block holds the same components (button, card, input,
  // banner, switch) and carries its Prism highlighted CSS.
  previewBlocks: {
    surface: string; card: string; text: string;
    accent: string; accentText: string; accent2: string; accent3: string;
    ratio: number; wcagLvl: string; wcagStatus: string;
    apca: number; apcaLvl: string; apcaStatus: string;
    rules: { key: string; html: SafeHtml }[]; cssText: string;
  }[] = [];

  // Which preview alternative is shown (0 = highest WCAG); the shuffle button
  // cycles through them.
  activePreview = 0;

  prevPreview(): void {
    if ( this.activePreview > 0 ) {
      this.activePreview--;
      this.analyticsService.logEvent( 'Palette Preview Changed', { direction: 'prev', index: this.activePreview } );
    }
  }

  shufflePreview(): void {
    if ( this.activePreview < this.previewBlocks.length - 1 ) {
      this.activePreview++;
      this.analyticsService.logEvent( 'Palette Preview Changed', { direction: 'next', index: this.activePreview } );
    }
  }

  // The preview component currently hovered, so the matching CSS rule lights up.
  hoveredRule: string | null = null;

  // CSS code-window editor + syntax colours. Loaded the same way the color tool
  // does: the active IDE-skin theme's palette (premium = bundled), else the
  // per-mode default palette, so the Prism highlighting matches the color page.
  private themeSub?: Subscription;
  private darkLightSub?: Subscription;
  private currentSelection: ProductThemeSelection | null = null;
  private darkLight = 'auto';
  editorBg = '';
  editorFg = '';
  synComment = '';
  synSelector = '';
  synProperty = '';
  synValue = '';
  synKeyword = '';
  synPunctuation = '';

  // Three random blog posts, reshuffled per load.
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );
  /** the How It Works closer under the blog (the server prints the same four questions as FAQPage) */
  readonly about = PALETTE_ABOUT;

  // Color Conversions section: one row per colour space (the same full list as
  // the color tool), each row listing EVERY palette colour in that space on a
  // single line, copyable in one click. Always a single column.
  paletteConversionGroups: { title: string; rows: { label: string; value: string; key: string }[] }[] = [];
  copiedKey = '';
  private copiedTimer: any;

  foundTheme: Product;

  // Banded backdrop for the hero, built from the current palette colors. Shown
  // (via the `striped-bg` class) only when no theme/image backs the hero -
  // mirrors the color page treatment.
  heroBackground = '';

  harmonyResult = [];

  uploadedImg;
  initializer;
  // Base36 seed of a full-random palette (shown as the Source, reproduces it).
  fullSeed = '';
  // How the current palette was generated (theme / a harmony scheme / warm /
  // cool / full random), used for the hero subtitle.
  scheme = '';

  analogous = [];
  complementaries = [];
  triadics = [];
  temperatures = [];

  // Per-scheme harmonic scores, listed in the SAME order as the color tool's
  // Harmony section (Analogous, Monochrome, Complementary, Split Complementary,
  // Triadic, Tetradic). score = the share of palette colours that take part
  // in at least one matching combination on the hue wheel; counts[i] is how many
  // combinations colour i joins (drives the swatch muting + badge).
  harmonyScores: { name: string; description: string; score: number; counts: number[] }[] = [];

  // Shades / Tints / Tones: under each sub-heading a few stacked rows, each row
  // the WHOLE palette pushed one step further toward white / black / gray.
  shadeTintTone: { title: string; rows: string[][] }[] = [];

  // Temperature: under Warmer / Cooler, the original palette on top then a few
  // rows stepping a little further toward red / cyan.
  temperatureVariants: { title: string; rows: string[][] }[] = [];

  protanomalyColors = [];
  deuteranomalyColors = [];
  tritanomalyColors = [];
  protanopiaColors = [];
  deuteranopiaColors = [];
  tritanopiaColors = [];
  achromatomalyColors = [];
  achromatopsiaColors = [];

  newColor = '#FFFFFF';

  selectedColorBlindnessSubType = 'protanomaly';
  selectedColorBlindnessObject = {
    title: '',
    explanation: '',
    colors: [],
  };

  private colorChangeSubject = new Subject<string>();

  constructor( private route: ActivatedRoute, public router: Router, private location: Location, private seoService: SeoService, private productService: ProductService, private colorService: ColorService, private clipboard: Clipboard, private analyticsService: AnalyticsService, private zone: NgZone, private sanitizer: DomSanitizer, private http: HttpClient, private themeService: ThemeService, private paletteGen: PaletteGenerationService ) {
    this.colorChangeSubject.pipe(
      debounceTime( 1000 ) // debounce time of 1 second
    ).subscribe( () => {
      this.updatePath();
    } );
  }

  ngOnInit(): void {
    this.uploadedImg = undefined;

    // Code-window scheme follows the active IDE-skin theme (same as the color
    // tool); recompute when the theme or the dark/light mode changes.
    this.themeSub = this.themeService.productTheme.subscribe( selection => {
      this.currentSelection = selection;
      this.applyCodeScheme();
    } );
    this.darkLightSub = this.themeService.darkLightSetting.subscribe( mode => {
      this.darkLight = mode;
      if ( !this.currentSelection ) {
        this.applyCodeScheme();
      }
    } );

    this.route.params.subscribe( ( params ) => {
      if ( params.palette ) {
        const palette = params.palette.split( '-' );

        if ( palette.length < 3 || palette.length > 10 ) {
          this.router.navigate( [ URLS.notFound ] );
          return;
        }

        this.palette = [];
        for ( const item of palette ) {
          if ( !this.colorService.isValidHexCode( item ) ) {
            this.router.navigate( [ URLS.notFound ] );
            return;
          }
          this.palette.push( { value: '#' + item.toLowerCase() } );
        }

        // On the first load (the random landing, or a shared /<hexes> link),
        // reflect the colour count in the Range field. Set here rather than in the
        // blank-visit branch because that branch re-navigates to /<hexes>, which
        // recreates this component (two-path route) and would reset count to 10.
        // Guarded so later edits don't keep rewriting it.
        if ( !this.countSynced ) {
          this.count = this.palette.length;
          this.countSynced = true;
        }

        // Don't clear the theme here: the query-params subscription owns the
        // subtitle source and would otherwise race with this on a fresh load.
        this.updateColors( false );
        this.refreshSeo();

      } else {

        // Blank visit: start from a random theme or a random scheme (same logic
        // the home widget uses).
        const start = this.paletteGen.randomStart( PALETTE_SCHEME_MINS );
        const params = start.hexes.map( h => h.replace( '#', '' ) ).join( '-' );
        let queryParams: { theme?: string; scheme?: string; initializer?: string; seed?: string };
        if ( start.theme ) {
          queryParams = { theme: start.theme.key };
        } else {
          queryParams = { scheme: start.scheme };
          if ( start.initializer ) { queryParams.initializer = start.initializer; }
          if ( start.seed ) { queryParams.seed = start.seed; }
        }
        // Render the random start IN PLACE (address bar stays on the bare
        // /tools/palette), so crawlers never see a JS redirect off the landing.
        this.router.navigate( [ URLS.tools.palette + '/' + params ], { queryParams, skipLocationChange: true, state: { scrollToTop: false } } );
      }
    } );

    // The query params are the single source of truth for the hero subtitle
    // (theme / harmony-seed / scheme). Set AND clear each, so this never races
    // with updateColors() (which the palette-param load below calls with
    // removeFoundTheme=false, leaving the subtitle entirely to this block).
    this.route.queryParams.subscribe( params => {
      if ( params.theme ) {
        const themes = THEMES.map( productLine => productLine.products ).reduce( ( acc, val ) => acc.concat( val ), [] );
        this.foundTheme = themes.find( product => product.key === params.theme );
      } else {
        this.foundTheme = undefined;
      }

      if ( params.initializer ) {
        this.initializer = {
          url: params.initializer,
          colorName: params.initializer.replace( new RegExp( '-', 'g' ), ' ' ),
          value: HEX_CODES[ params.initializer ] ? '#' + HEX_CODES[ params.initializer ] : ''
        };
      } else {
        this.initializer = undefined;
      }

      this.scheme = params.scheme || '';
      this.fullSeed = params.seed || '';

      this.refreshSeo();
    } );
  }

  onColorChange( value: string ) {
    this.updateColors();
    this.colorChangeSubject.next( value );
  }

  /** the widget's reverse button: the palette in the opposite order */
  reverseColors(): void {
    if ( this.palette.length < 2 ) return;
    this.palette.reverse();
    this.updatePath( false );
    this.updateColors( false );
    this.analyticsService.logEvent( 'Palette Reversed', { count: this.palette.length } );
  }

  drop( event: CdkDragDrop<string[]> ) {
    moveItemInArray( this.palette, event.previousIndex, event.currentIndex );
    this.updatePath( false );
    this.updateColors( false );
    this.analyticsService.logEvent( 'Palette Reordered', { count: this.palette.length } );
  }

  handleImageUpload( event: any ) {
    const file = event.target.files[ 0 ];
    if ( file ) {
      const reader = new FileReader();
      reader.onload = async ( e: any ) => {
        const img = new Image();
        img.src = e.target.result;
        img.id = file.name;
        img.onload = async () => {
          try {
            const options = {
              pixels: 500,
              distance: 0.1,
              saturationDistance: 0,
              lightnessDistance: 0,
              hueDistance: 0
            };

            // Extract colors from the original image. extract-colors can settle
            // outside Angular's zone, leaving the palette + analysis stale; run the
            // state update inside the zone so change detection always fires.
            await extractColors( img.src, options ).then( ( colors ) => this.zone.run( () => {
              colors.sort( ( a, b ) => b.area - a.area );
              if ( colors.length > this.count ) {
                colors = colors.slice( 0, this.count );
              }
              if ( colors.length < 3 ) {
                do {
                  colors.push( colors[ 0 ] );
                } while ( colors.length < 3 );
              }
              this.palette = colors.map( ( color ) => {
                return { value: color.hex };
              } );

              this.colorService.sortByHue( this.palette );

              // Now proceed to resize the image. It doubles as the hero backdrop
              // (shown sharp, full width), so keep it large enough to stay crisp.
              const maxWidth = 1280; // Maximum width for the displayed image
              const maxHeight = 960; // Maximum height for the displayed image

              let width = img.width;
              let height = img.height;

              // Calculate the new dimensions while maintaining aspect ratio
              if ( width > maxWidth || height > maxHeight ) {
                if ( width / height > maxWidth / maxHeight ) {
                  height = ( maxWidth / width ) * height;
                  width = maxWidth;
                } else {
                  width = ( maxHeight / height ) * width;
                  height = maxHeight;
                }
              }

              // Create a canvas element to draw the resized image
              const canvas = document.createElement( 'canvas' );
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext( '2d' );

              if ( ctx ) {
                ctx.drawImage( img, 0, 0, width, height );
                // Get the resized image as a Data URL
                const resizedImgData = canvas.toDataURL( 'image/jpeg', 0.85 ); // Adjust quality as needed

                // Set the resized image for display
                this.uploadedImg = new Image();
                this.uploadedImg.id = file.name;
                this.uploadedImg.src = resizedImgData; // Use the resized image data
                this.initializer = undefined;

                this.updatePath();
                this.updateColors();
                this.analyticsService.logEvent( 'Palette From Image', { colors: this.palette.length } );
              }
            } ) );
          } catch ( error ) {
            console.error( 'Error generating color palette:', error );
          }
        };
      };
      reader.readAsDataURL( file );
    }
  }

  public analyzePaletteHarmony() {
    const rgbPalette = this.palette.map( color => this.colorService.hexToRgb( color.value ) );

    const hsvPalette = rgbPalette.map( rgb => this.colorService.rgbToHsv( rgb ) );

    const hues = hsvPalette.map( hsv => hsv.h );

    let complementaryCount = 0;
    let analogousCount = 0;
    let triadicCount = 0;
    let warmCount = 0;
    let coolCount = 0;

    const huePairs = new Set();
    const hueTriplets = new Set();

    this.temperatures = [];
    this.analogous = [];
    this.complementaries = [];
    this.triadics = [];
    for ( let i = 0; i < hues.length; i++ ) {
      this.analogous.push( 0 );
      this.complementaries.push( 0 );
      this.triadics.push( 0 );
    }

    // Combine loops for hue comparison and warm/cool check
    for ( let i = 0; i < hues.length; i++ ) {
      // Check if the hue is warm or cool
      if ( this.colorService.isWarm( hues[ i ] ) ) {
        warmCount++;
        this.temperatures.push( 'warm' );
      } else if ( this.colorService.isCool( hues[ i ] ) ) {
        this.temperatures.push( 'cool' );
        coolCount++;
      }

      for ( let j = i + 1; j < hues.length; j++ ) {
        if ( !huePairs.has( `${ i },${ j }` ) && !huePairs.has( `${ j },${ i }` ) ) {
          if ( this.colorService.isComplementary( hues[ i ], hues[ j ] ) ) {
            this.complementaries[ i ] = this.complementaries[ i ] + 1;
            this.complementaries[ j ] = this.complementaries[ j ] + 1;
          }
          if ( this.colorService.isAnalogous( hues[ i ], hues[ j ] ) ) {
            this.analogous[ i ] = this.analogous[ i ] + 1;
            this.analogous[ j ] = this.analogous[ j ] + 1;
          }
          huePairs.add( `${ i },${ j }` );
        }
      }
    }

    for ( let i = 0; i < hues.length - 2; i++ ) {
      for ( let j = i + 1; j < hues.length - 1; j++ ) {
        for ( let k = j + 1; k < hues.length; k++ ) {
          const triplet = [ hues[ i ], hues[ j ], hues[ k ] ].sort().join( ',' );
          if ( !hueTriplets.has( triplet ) ) {
            if ( this.colorService.isTriadic( [ hues[ i ], hues[ j ], hues[ k ] ] ) ) {
              this.triadics[ i ] = this.triadics[ i ] + 1;
              this.triadics[ j ] = this.triadics[ j ] + 1;
              this.triadics[ k ] = this.triadics[ k ] + 1;
            }
            hueTriplets.add( triplet );
          }
        }
      }
    }

    for ( let i = 0; i < hues.length; i++ ) {
      analogousCount += this.analogous[ i ];
      complementaryCount += this.complementaries[ i ];
      triadicCount += this.triadics[ i ];
    }

    const temperatureCount = Math.max( warmCount, coolCount );

    let temperatureDecision = '';

    if ( warmCount > coolCount ) {
      temperatureDecision = 'warm';
    } else if ( warmCount < coolCount ) {
      temperatureDecision = 'cool';
    } else {
      temperatureDecision = 'equal';
    }

    return [
      {
        name: 'Analogous',
        count: analogousCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2,
      },
      {
        name: 'Complementary',
        count: complementaryCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2,
      },
      {
        name: 'Triadic',
        count: triadicCount,
        max: ( this.palette.length * ( this.palette.length - 1 ) * ( this.palette.length - 2 ) ) / 6,
      },
      {
        name: 'Temperature',
        count: temperatureCount,
        decision: temperatureDecision,
        max: this.palette.length,
      },
    ];
  }

  updateColors( removeFoundTheme = true ) {
    if ( removeFoundTheme === true ) {
      this.foundTheme = undefined;
    }
    this.protanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanomaly' );
    this.deuteranomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranomaly' );
    this.tritanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanomaly' );
    this.protanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanopia' );
    this.deuteranopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranopia' );
    this.tritanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanopia' );
    this.achromatomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatomaly' );
    this.achromatopsiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatopsia' );
    this.harmonyResult = this.analyzePaletteHarmony();
    this.buildHarmonyScores();
    this.buildShadeTintTone();
    this.buildTemperature();
    this.setSelectedColorBlindnessColors();
    this.buildHeroBackground();
    this.buildRelatedThemes();
    this.buildConversions();
    this.buildChannelStats();
    this.buildPreview();
  }

  // Build the Preview section: five DIFFERENT palette role assignments, each a
  // full interface block, ranked from the best to the worst WCAG contrast. Using
  // several assignments puts more of the palette to work. Each block carries its
  // own Prism highlighted CSS for the panel on the right.
  private buildPreview(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length < 2 ) {
      this.previewBlocks = [];
      this.activePreview = 0;
      return;
    }
    const cr = ( a: string, b: string ) => this.colorService.contrastRatio( a, b );
    const sat = ( h: string ) => this.colorService.hexToHsl( h.replace( '#', '' ) ).s;
    const bySat = [ ...hexes ].sort( ( a, b ) => sat( b ) - sat( a ) );
    // Best-contrast palette colour against a base (every colour stays in-palette).
    const bestOn = ( base: string ) => {
      let best = hexes[ 0 ], bv = -1;
      for ( const h of hexes ) { const c = cr( h, base ); if ( c > bv ) { bv = c; best = h; } }
      return best;
    };

    // Score every distinct surface/card pairing by the best in-palette text
    // readability against both surfaces, by WCAG AND APCA together, rank
    // best-first, and keep the top alternatives to cycle through.
    const apcaMag = ( a: string, b: string ) => Math.abs( this.colorService.apcaContrast( a, b ) );
    const candidates: { surface: string; card: string; text: string; ratio: number; score: number }[] = [];
    for ( const surface of hexes ) {
      for ( const card of hexes ) {
        if ( surface === card ) { continue; }
        let text = hexes[ 0 ], ratio = -1;
        for ( const h of hexes ) {
          const v = Math.min( cr( h, surface ), cr( h, card ) );
          if ( v > ratio ) { ratio = v; text = h; }
        }
        const ap = Math.min( apcaMag( text, surface ), apcaMag( text, card ) );
        // Normalised WCAG (max 21) + normalised APCA (max ~108), so the first
        // shown maximises both.
        const score = ratio / 21 + ap / 108;
        candidates.push( { surface, card, text, ratio, score } );
      }
    }
    candidates.sort( ( a, b ) => b.score - a.score );
    const pairs = candidates.slice( 0, 20 );

    this.activePreview = 0;
    this.previewBlocks = pairs.map( ( { surface, card, text, ratio } ) => {
      // Distinct accents so a richer palette puts more colours to work: the
      // button keeps the lead accent, switches take a second, banner/input
      // borders a third (only when the palette has the colours to spare).
      const accent = bySat.find( h => h !== surface && h !== card ) || bySat[ 0 ];
      const accent2 = hexes.length > 3
        ? ( bySat.find( h => h !== surface && h !== card && h !== accent ) || accent )
        : accent;
      const accent3 = hexes.length > 4
        ? ( bySat.find( h => h !== surface && h !== card && h !== accent && h !== accent2 ) || accent )
        : accent;
      const accentText = bestOn( accent );
      const c = { surface, card, text, accent, accentText, accent2, accent3, ratio };
      // One rule per component so hovering a component in the mockup lights up its
      // matching rule. The base text colour is set once on `.text`; the card and
      // input share it by inheritance, so it is not repeated on every rule.
      const ruleDefs: { key: string; css: string }[] = [
        { key: 'text', css: `.text {\n  color: ${ c.text };\n}` },
        { key: 'card', css: `.card {\n  background: ${ c.card };\n}` },
        { key: 'banner', css: `.banner {\n  border-left: 4px solid ${ c.accent3 };\n  background: ${ c.card };\n}` },
        { key: 'input', css: `.input {\n  background: ${ c.card };\n  border: 1px solid ${ c.accent3 };\n}` },
        { key: 'toggle', css: `.toggle {\n  background: ${ c.accent2 };\n}` },
        { key: 'btn', css: `.btn {\n  background: ${ c.accent };\n  color: ${ c.accentText };\n}` },
        { key: 'btn-outline', css: `.btn.outline {\n  background: none;\n  border: 1px solid ${ c.accent };\n}` },
      ];
      const rules = ruleDefs.map( r => ( {
        key: r.key,
        html: this.sanitizer.bypassSecurityTrustHtml( addColorSwatches( Prism.highlight( r.css, Prism.languages.css, 'css' ) ) ),
      } ) );
      const cssText = ruleDefs.map( r => r.css ).join( '\n\n' );
      const wcagLvl = this.colorService.wcagLevel( c.ratio );
      const wcagStatus = wcagLvl === 'AAA' ? 'ok' : wcagLvl === 'AA' ? 'warn' : 'fail';
      // APCA Lc for the text over both surfaces; report the weaker one.
      const apcaA = this.colorService.apcaContrast( c.text, c.surface );
      const apcaB = this.colorService.apcaContrast( c.text, c.card );
      const apca = Math.abs( apcaA ) <= Math.abs( apcaB ) ? apcaA : apcaB;
      const apcaMag = Math.abs( apca );
      const apcaStatus = apcaMag >= 75 ? 'ok' : apcaMag >= 45 ? 'warn' : 'fail';
      return {
        surface: c.surface,
        card: c.card,
        text: c.text,
        accent: c.accent,
        accentText: c.accentText,
        accent2: c.accent2,
        accent3: c.accent3,
        ratio: Math.round( c.ratio * 10 ) / 10,
        wcagLvl,
        wcagStatus,
        apca: Math.round( apcaMag ),
        apcaLvl: this.colorService.apcaLevel( apca ),
        apcaStatus,
        rules,
        cssText,
      };
    } );
  }

  // Aggregate the palette into overall channel stats: each RGB channel's share of
  // the total channel sum ("weight"), and the average hue (circular mean),
  // saturation and lightness across all colours.
  private buildChannelStats(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length === 0 ) {
      this.channelStats = null;
      return;
    }
    let sumR = 0, sumG = 0, sumB = 0, sumS = 0, sumL = 0, hx = 0, hy = 0;
    let sumSv = 0, sumV = 0, sumC = 0, sumM = 0, sumY = 0, sumK = 0;
    for ( const hex of hexes ) {
      const num = parseInt( hex.replace( '#', '' ), 16 );
      const r = ( num >> 16 ) & 255;
      const g = ( num >> 8 ) & 255;
      const b = num & 255;
      sumR += r;
      sumG += g;
      sumB += b;
      const hsl = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      sumS += hsl.s;
      sumL += hsl.l;
      const rad = ( hsl.h * Math.PI ) / 180;
      hx += Math.cos( rad );
      hy += Math.sin( rad );

      // HSV (value + saturation) and CMYK, straight from the 0-255 channels.
      const rf = r / 255, gf = g / 255, bf = b / 255;
      const max = Math.max( rf, gf, bf ), min = Math.min( rf, gf, bf );
      sumV += max * 100;
      sumSv += ( max === 0 ? 0 : ( max - min ) / max ) * 100;
      const k = 1 - max;
      sumK += k * 100;
      if ( k < 1 ) {
        sumC += ( ( 1 - rf - k ) / ( 1 - k ) ) * 100;
        sumM += ( ( 1 - gf - k ) / ( 1 - k ) ) * 100;
        sumY += ( ( 1 - bf - k ) / ( 1 - k ) ) * 100;
      }
    }
    const n = hexes.length;
    const total = ( sumR + sumG + sumB ) || 1;
    let avgH = ( Math.atan2( hy / n, hx / n ) * 180 ) / Math.PI;
    if ( avgH < 0 ) { avgH += 360; }
    const avgS = sumS / n;
    const avgL = sumL / n;
    const avgSv = sumSv / n;
    const avgV = sumV / n;
    const avgC = sumC / n, avgM = sumM / n, avgYy = sumY / n, avgK = sumK / n;
    const weight = ( x: number ) => Math.round( ( x / total ) * 100 );
    this.channelStats = {
      rgb: [
        { label: 'R', width: weight( sumR ), text: weight( sumR ) + '%', color: '#e0584f' },
        { label: 'G', width: weight( sumG ), text: weight( sumG ) + '%', color: '#3fb27a' },
        { label: 'B', width: weight( sumB ), text: weight( sumB ) + '%', color: '#4a86e8' },
      ],
      hsl: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgS, text: Math.round( avgS ) + '%', color: `hsl(${ avgH }, ${ avgS }%, 55%)` },
        { label: 'L', width: avgL, text: Math.round( avgL ) + '%', color: `hsl(0, 0%, ${ avgL }%)` },
      ],
      hsv: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgSv, text: Math.round( avgSv ) + '%', color: `hsl(${ avgH }, ${ avgSv }%, 55%)` },
        { label: 'V', width: avgV, text: Math.round( avgV ) + '%', color: `hsl(${ avgH }, 40%, ${ Math.max( 20, avgV ) }%)` },
      ],
      cmyk: [
        { label: 'C', width: avgC, text: Math.round( avgC ) + '%', color: '#22b3c8' },
        { label: 'M', width: avgM, text: Math.round( avgM ) + '%', color: '#cf2e8a' },
        { label: 'Y', width: avgYy, text: Math.round( avgYy ) + '%', color: '#e6c100' },
        { label: 'K', width: avgK, text: Math.round( avgK ) + '%', color: '#3a3a3a' },
      ],
    };
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    // Scroll-spy: highlight the sidebar entry for whichever section is centered.
    this.sectionObserver = new IntersectionObserver( entries => {
      let changed = false;
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
            changed = true;
          }
        }
      }
      if ( changed ) {
        this.scrollActivePillToStart();
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.themeSub?.unsubscribe();
    this.darkLightSub?.unsubscribe();
  }

  // Pick the code-window scheme exactly like the color tool: a premium theme uses
  // bundled colours, a free theme its public palette.json, no skin the default.
  private applyCodeScheme(): void {
    const sel = this.currentSelection;
    if ( sel?.key && CSS_SYNTAX_PREMIUM[ sel.key ] ) {
      this.applyCssSyntax( CSS_SYNTAX_PREMIUM[ sel.key ] );
    } else if ( sel?.key && sel?.category ) {
      this.loadThemePalette( sel.category, sel.key );
    } else {
      this.loadDefaultPalette();
    }
  }

  // Fetch the active theme's palette.json and map its JetBrains CSS syntax
  // colours onto the code block (mirrors the color tool / theme detail page).
  private loadThemePalette( category: string, key: string ): void {
    const url = `assets/themes/${ category }/${ key }-theme/${ key }.palette.json`;
    this.http.get<{ tokens?: any }>( url ).subscribe( {
      next: palette => this.applyThemePalette( palette?.tokens ),
      error: () => this.applyCssSyntax( CSS_SYNTAX_DEFAULT ),
    } );
  }

  // Code-window colours: the same neutral default scheme the color tool uses (a
  // bundled emergency scheme first, then the per-mode default palette JSON), so
  // the Prism hex/value colours match the color page rather than the bright accent.
  private loadDefaultPalette(): void {
    this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
    const mode = this.isDarkMode() ? 'dark' : 'light';
    this.http.get<{ tokens?: any }>( `assets/themes/default/default-${ mode }.palette.json` ).subscribe( {
      next: palette => {
        if ( palette?.tokens ) {
          this.applyThemePalette( palette.tokens );
        }
      },
      error: () => { /* keep the bundled emergency default */ },
    } );
  }

  private isDarkMode(): boolean {
    if ( this.darkLight === 'dark' ) { return true; }
    if ( this.darkLight === 'light' ) { return false; }
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia( '(prefers-color-scheme: dark)' ).matches;
  }

  private applyCssSyntax( t: CssSyntaxTheme ): void {
    this.editorBg = t.editorBackground;
    this.editorFg = t.editorForeground;
    this.synComment = t.comment;
    this.synSelector = t.selector;
    this.synProperty = t.property;
    this.synValue = t.value;
    this.synKeyword = t.keyword;
    this.synPunctuation = t.punctuation;
  }

  private applyThemePalette( tokens: any ): void {
    if ( !tokens ) {
      this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
      return;
    }
    const css = tokens.css ?? {};
    const semantic = tokens.semantic ?? {};
    this.editorBg = tokens.editor?.background ?? tokens.interface?.editorBackground ?? '';
    this.editorFg = tokens.editor?.foreground ?? semantic.plainText ?? '';
    this.synComment = css.comment ?? semantic.comment ?? '';
    this.synSelector = css.selector ?? semantic.selector ?? semantic.tag ?? '';
    this.synProperty = css.property ?? semantic.property ?? semantic.field ?? '';
    this.synValue = css.number ?? css.string ?? semantic.number ?? semantic.string ?? semantic.attributeValue ?? '';
    this.synKeyword = css.keyword ?? semantic.keyword ?? '';
    this.synPunctuation = css.punctuation ?? semantic.punctuation ?? semantic.operator ?? '';
  }

  /** Hard-edged equal bands of the current palette for the sidebar swatch, so it
   *  reads as discrete color steps (like the palette tool icon) rather than a
   *  smooth blend. Each color gets two stops at the same boundary to keep the
   *  transition crisp. */
  paletteGradient(): string {
    const colors = this.pickHeroColors();
    if ( colors.length === 0 ) {
      return 'var(--darker-contrast)';
    }
    if ( colors.length === 1 ) {
      return colors[ 0 ];
    }
    const band = 100 / colors.length;
    const stops = colors
      .map( ( c, i ) => `${ c } ${ ( i * band ).toFixed( 3 ) }%, ${ c } ${ ( ( i + 1 ) * band ).toFixed( 3 ) }%` )
      .join( ', ' );
    return `linear-gradient(90deg, ${ stops })`;
  }

  // Smooth-scroll to a section, clearing the fixed header (and, on mobile, the
  // sticky section bar). Mirrors the color tool page.
  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    // clear the nav and the sticky chips' bar under it (owner 2026-10-12)
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-chipbar-row' ) as HTMLElement | null;
      if ( !strip || strip.scrollWidth <= strip.clientWidth ) {
        return;
      }
      const active = strip.querySelector( '.pl-chip.on' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }

  // Rank IDE themes by how closely their palette covers this palette: for each of
  // our colors, take the nearest theme colour (hue-dominant distance), average
  // those, and keep the three closest themes.
  private buildRelatedThemes(): void {
    const mine = this.pickHeroColors().map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ) );
    if ( mine.length === 0 ) {
      this.relatedThemeList = [];
      return;
    }
    const dist = ( a: any, b: any ): number => {
      let dh = Math.abs( a.h - b.h );
      if ( dh > 180 ) { dh = 360 - dh; }
      const satW = Math.min( a.s, b.s ) / 100;
      const hueTerm = dh * satW;
      return Math.sqrt( hueTerm * hueTerm + 0.12 * ( a.s - b.s ) * ( a.s - b.s ) + 0.10 * ( a.l - b.l ) * ( a.l - b.l ) );
    };
    const scored: { product: Product; category: string; d: number }[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        const pal = ( theme.palette || [] )
          .filter( ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) ) )
          .map( ( p: string ) => this.colorService.hexToHsl( p.replace( '#', '' ) ) );
        if ( pal.length === 0 ) { continue; }
        let sum = 0;
        for ( const c of mine ) {
          let best = Infinity;
          for ( const t of pal ) {
            const d = dist( c, t );
            if ( d < best ) { best = d; }
          }
          sum += best;
        }
        scored.push( { product: theme, category: line.name, d: sum / mine.length } );
      }
    }
    scored.sort( ( a, b ) => a.d - b.d );
    // Always the closest theme (min 1), plus any others that are nearly as close
    // (within a small absolute margin of the best), capped at 3. So an exact or
    // distinctive palette returns just its one true match, while a palette that
    // genuinely sits between several themes shows up to three.
    const best = scored.length ? scored[ 0 ].d : 0;
    const MARGIN = 1;
    const close = scored.filter( ( s, i ) => i === 0 || s.d <= best + MARGIN );
    this.relatedThemeList = close.slice( 0, 3 ).map( ( { d, ...rest } ) => rest );
  }

  // Build the conversion rows: one row per colour space (the full color-tool
  // list), each value being every palette colour in that space joined on one
  // line. Copying a row grabs the whole comma-separated set.
  private buildConversions(): void {
    const hexes = this.pickHeroColors();
    const cs = this.colorService;
    const join = ( fn: ( h: string ) => string ): string => hexes.map( fn ).join( ', ' );

    const rgb = ( h: string ) => { const i = cs.hexToRgb( h ).items; return `rgb(${ i[ 0 ].value }, ${ i[ 1 ].value }, ${ i[ 2 ].value })`; };
    const rgbp = ( h: string ) => { const [ r, g, b ] = cs.rgbToPercent( h ); return `rgb(${ r }%, ${ g }%, ${ b }%)`; };
    const hsl = ( h: string ) => { const x = cs.hexToHsl( h.replace( '#', '' ) ); return `hsl(${ x.h }, ${ Math.round( x.s ) }%, ${ Math.round( x.l ) }%)`; };
    const hsv = ( h: string ) => { const [ a, b, c ] = cs.hexToHsv( h ); return `hsv(${ a }, ${ b }%, ${ c }%)`; };
    const hsb = ( h: string ) => { const [ a, b, c ] = cs.hexToHsv( h ); return `hsb(${ a }, ${ b }%, ${ c }%)`; };
    const hwb = ( h: string ) => { const [ a, b, c ] = cs.hexToHwb( h ); return `hwb(${ a } ${ b }% ${ c }%)`; };
    const cmyk = ( h: string ) => { const i = cs.hexToCmyk( h ).items; return `cmyk(${ i[ 0 ].value }%, ${ i[ 1 ].value }%, ${ i[ 2 ].value }%, ${ i[ 3 ].value }%)`; };
    const p3 = ( h: string ) => { const [ r, g, b ] = cs.hexToDisplayP3( h ); return `color(display-p3 ${ r } ${ g } ${ b })`; };
    const oklch = ( h: string ) => { const [ l, c, hh ] = cs.hexToOklch( h ); return `oklch(${ l } ${ c } ${ hh })`; };
    const oklab = ( h: string ) => { const [ l, a, b ] = cs.hexToOklab( h ); return `oklab(${ l } ${ a } ${ b })`; };
    const lab = ( h: string ) => { const [ l, a, b ] = cs.hexToLab( h ); return `lab(${ l } ${ a } ${ b })`; };
    const lch = ( h: string ) => { const [ l, c, hh ] = cs.hexToLch( h ); return `lch(${ l } ${ c } ${ hh })`; };
    const xyz = ( h: string ) => { const [ x, y, z ] = cs.hexToXyz( h ).map( ( n: number ) => Math.round( n * 1000 ) / 1000 ); return `xyz(${ x }, ${ y }, ${ z })`; };
    const yxy = ( h: string ) => { const [ Y, x, y ] = cs.hexToYxy( h ); return `yxy(${ Y }, ${ x }, ${ y })`; };
    const luv = ( h: string ) => { const [ l, u, v ] = cs.hexToLuv( h ); return `luv(${ l }, ${ u }, ${ v })`; };
    const hlab = ( h: string ) => { const [ l, a, b ] = cs.hexToHunterLab( h ); return `${ l }, ${ a }, ${ b }`; };

    // Same colour-space grouping as the color tool's Conversions table (web-safe
    // stays under Web & sRGB). One column here because each value lists the whole
    // palette on a single line.
    this.paletteConversionGroups = [
      {
        title: 'Web & sRGB',
        rows: [
          { label: 'Hex', value: hexes.map( ( h: string ) => h.toUpperCase() ).join( ', ' ), key: 'pc-hex' },
          { label: 'RGB', value: join( rgb ), key: 'pc-rgb' },
          { label: 'RGB %', value: join( rgbp ), key: 'pc-rgbp' },
          { label: 'Web-Safe', value: join( h => cs.nearestWebSafe( h ).toUpperCase() ), key: 'pc-websafe' },
          { label: 'Decimal', value: join( h => String( cs.hexToDecimalInt( h ) ) ), key: 'pc-dec' },
          { label: 'Octal', value: join( h => cs.hexToOctal( h ) ), key: 'pc-octal' },
          { label: 'Binary', value: join( h => cs.hexToBinary( h ) ), key: 'pc-bin' },
        ],
      },
      {
        title: 'Hue-based',
        rows: [
          { label: 'HSL', value: join( hsl ), key: 'pc-hsl' },
          { label: 'HSV', value: join( hsv ), key: 'pc-hsv' },
          { label: 'HSB', value: join( hsb ), key: 'pc-hsb' },
          { label: 'HWB', value: join( hwb ), key: 'pc-hwb' },
        ],
      },
      {
        title: 'Print',
        rows: [
          { label: 'CMYK', value: join( cmyk ), key: 'pc-cmyk' },
          { label: 'RAL', value: join( h => cs.nearestRalCode( h ) ), key: 'pc-ral' },
        ],
      },
      {
        title: 'Perceptual & wide gamut',
        rows: [
          { label: 'OKLCH', value: join( oklch ), key: 'pc-oklch' },
          { label: 'OKLAB', value: join( oklab ), key: 'pc-oklab' },
          { label: 'CIELAB', value: join( lab ), key: 'pc-lab' },
          { label: 'CIELCH', value: join( lch ), key: 'pc-lch' },
          { label: 'CIELUV', value: join( luv ), key: 'pc-luv' },
          { label: 'Display P3', value: join( p3 ), key: 'pc-p3' },
        ],
      },
      {
        title: 'CIE tristimulus',
        rows: [
          { label: 'XYZ', value: join( xyz ), key: 'pc-xyz' },
          { label: 'Yxy', value: join( yxy ), key: 'pc-yxy' },
          { label: 'Hunter Lab', value: join( hlab ), key: 'pc-hlab' },
        ],
      },
    ];
  }

  copyText( text: string, key: string ): void {
    this.clipboard.copy( text );
    this.analyticsService.logEvent( 'Palette Exported', { format: key } );
    this.copiedKey = key;
    if ( this.copiedTimer ) { clearTimeout( this.copiedTimer ); }
    this.copiedTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  // Score each colour scheme the way the color tool's Harmony section lists them
  // (Analogous, Monochrome, Complementary, Split Complementary, Triadic,
  // Tetradic). A scheme's score is the share of palette colours that take part
  // in at least one matching combination on the hue wheel; counts[i] is how many
  // combinations colour i joins (drives the swatch muting + the badge).
  private buildHarmonyScores(): void {
    const hexes = this.pickHeroColors();
    const n = hexes.length;
    const hues = hexes.map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ).h );

    // Shortest distance between two hues on the 360 degree wheel.
    const dist = ( a: number, b: number ): number => {
      const d = Math.abs( a - b ) % 360;
      return d > 180 ? 360 - d : d;
    };

    const pair = ( ok: ( a: number, b: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( hues[ i ], hues[ j ] ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const triple = ( ok: ( a: number, b: number, c: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            if ( ok( hues[ i ], hues[ j ], hues[ k ] ) ) { counts[ i ]++; counts[ j ]++; counts[ k ]++; }
          }
        }
      }
      return counts;
    };

    const quad = ( ok: ( a: number, b: number, c: number, d: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            for ( let l = k + 1; l < n; l++ ) {
              if ( ok( hues[ i ], hues[ j ], hues[ k ], hues[ l ] ) ) {
                counts[ i ]++; counts[ j ]++; counts[ k ]++; counts[ l ]++;
              }
            }
          }
        }
      }
      return counts;
    };

    const isComp = ( a: number, b: number ) => dist( a, b ) >= 156;
    // Monochrome and analogous are kept DISJOINT so they no longer score the same:
    // monochrome is the same hue (a tight band), analogous is a NEIGHBOURING but
    // distinct hue (just outside the monochrome band, up to 40 degrees away).
    const isMono = ( a: number, b: number ) => dist( a, b ) <= 12;
    const isAnalog = ( a: number, b: number ) => dist( a, b ) > 12 && dist( a, b ) <= 40;
    const isTriad = ( a: number, b: number, c: number ) =>
      dist( a, b ) >= 90 && dist( a, b ) <= 150 &&
      dist( b, c ) >= 90 && dist( b, c ) <= 150 &&
      dist( a, c ) >= 90 && dist( a, c ) <= 150;
    // A base sitting near the complement of two colours that flank it closely.
    const isSplit = ( a: number, b: number, c: number ) => {
      const tri = [ a, b, c ];
      for ( let base = 0; base < 3; base++ ) {
        const x = tri[ ( base + 1 ) % 3 ];
        const y = tri[ ( base + 2 ) % 3 ];
        if ( dist( tri[ base ], x ) >= 126 && dist( tri[ base ], x ) <= 174 &&
             dist( tri[ base ], y ) >= 126 && dist( tri[ base ], y ) <= 174 &&
             dist( x, y ) >= 36 && dist( x, y ) <= 90 ) {
          return true;
        }
      }
      return false;
    };
    // Two complementary pairs (a rectangle/square on the wheel).
    const isTetra = ( a: number, b: number, c: number, d: number ) => {
      const pairings = [
        [ [ a, b ], [ c, d ] ],
        [ [ a, c ], [ b, d ] ],
        [ [ a, d ], [ b, c ] ],
      ];
      return pairings.some( p => isComp( p[ 0 ][ 0 ], p[ 0 ][ 1 ] ) && isComp( p[ 1 ][ 0 ], p[ 1 ][ 1 ] ) );
    };

    // RGB chroma (max - min) per colour. Near-gray tints/shades have a tiny
    // chroma and an unreliable hue (8-bit rounding swings it by many degrees at
    // high lightness), but a tint or shade still belongs to a single-hue family.
    // So for mono / analogous we let a low-chroma colour match regardless of its
    // noisy hue - this is what made a pure monochrome palette score 90% not 100%.
    const chroma = hexes.map( h => {
      const v = parseInt( h.replace( '#', '' ), 16 );
      const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
      return Math.max( r, g, b ) - Math.min( r, g, b );
    } );
    const lowChroma = ( i: number ): boolean => chroma[ i ] <= 28;

    const pairIdx = ( ok: ( i: number, j: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( i, j ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const schemes: { name: string; description: string; counts: number[] }[] = [
      { name: 'Analogous', description: 'Neighbouring hues. Calm, cohesive, easy on the eyes.', counts: pairIdx( ( i, j ) => !lowChroma( i ) && !lowChroma( j ) && isAnalog( hues[ i ], hues[ j ] ) ) },
      { name: 'Monochrome', description: 'One hue, varied in lightness. Quiet, clean, simple to balance.', counts: pairIdx( ( i, j ) => ( lowChroma( i ) && lowChroma( j ) ) || isMono( hues[ i ], hues[ j ] ) ) },
      { name: 'Complementary', description: 'The opposite hue. Maximum contrast, one bold accent.', counts: pair( isComp ) },
      { name: 'Split Complementary', description: 'The two hues beside the opposite. Softer tension, more range.', counts: triple( isSplit ) },
      { name: 'Triadic', description: 'Three evenly spaced hues. Balanced but lively.', counts: triple( isTriad ) },
      { name: 'Tetradic', description: 'Two complementary pairs. The richest scheme, hardest to balance.', counts: quad( isTetra ) },
    ];

    this.harmonyScores = schemes.map( s => ( {
      name: s.name,
      description: s.description,
      // The number of palette colours that take part in this scheme (a plain count,
      // not a percentage, and not the number of combinations, so triadic's triplets
      // do not inflate it). Same measure across every tool and the theme page.
      score: s.counts.filter( c => c > 0 ).length,
      counts: s.counts,
    } ) );
  }

  // Under each sub-heading (Tints / Shades / Tones), a few stacked rows: each row
  // is the FULL palette pushed one step further toward white / black / gray, so
  // every row reads like the palette rows above, just a little more tinted.
  private buildShadeTintTone(): void {
    const hexes = this.pickHeroColors();
    const EXTRA = 3; // the original palette on top, then this many stepped rows
    const rowsToward = ( endHex: string ): string[][] => {
      // Per-colour ramp (index 0 is the base), transposed so each row is the
      // whole palette: row 0 is the original, rows below step toward the target.
      const ramps = hexes.map( base => this.colorService.generateShades( base, endHex, EXTRA + 1 ) );
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step ] ) );
      }
      return rows;
    };
    this.shadeTintTone = [
      { title: 'Tints', rows: rowsToward( 'ffffff' ) },
      { title: 'Shades', rows: rowsToward( '000000' ) },
      { title: 'Tones', rows: rowsToward( '808080' ) },
    ];
  }

  // Warmer / Cooler: the original palette on top, then a few rows each stepping
  // the whole palette a little further toward red / cyan.
  private buildTemperature(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length === 0 ) {
      this.temperatureVariants = [];
      return;
    }
    const EXTRA = 3; // the original palette on top, then this many stepped rows
    const STRIDE = 2; // skip steps so each row shifts visibly (the per-step warm/cool shift is now larger)
    const toRows = ( ramps: string[][] ): string[][] => {
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step * STRIDE ] ) );
      }
      return rows;
    };
    const span = EXTRA * STRIDE + 1;
    this.temperatureVariants = [
      { title: 'Warmer', rows: toRows( hexes.map( h => this.colorService.generateWarmerColors( h, span ) ) ) },
      { title: 'Cooler', rows: toRows( hexes.map( h => this.colorService.generateCoolerColors( h, span ) ) ) },
    ];
  }

  // Status level for a harmony score, mirroring the readability dots: strong
  // (green) / partial (amber) / weak (red). A 0 score is weak, so it reads red.
  // Dot level from the share of the palette that takes part (count / total), so the
  // three dot sizes stay meaningful whatever the palette length.
  harmonyLevel( count: number, total: number ): string {
    const frac = total > 0 ? count / total : 0;
    if ( frac >= 0.67 ) { return 'high'; }
    if ( frac >= 0.34 ) { return 'mid'; }
    return 'low';
  }

  // Colour-vision simulations grouped by clinical category (mirrors the color
  // tool page's grouping).
  // Grouped by the affected cone (protan / deutan / tritan, plus monochromacy),
  // each family read mild (anomaly) to severe (anopia). Each row is captioned in
  // one line: bold name, then its description inline in a lighter style.
  colorVisionGroups(): { group: string; items: { name: string; note: string; colors: string[] }[] }[] {
    return [
      {
        group: 'Protan (red)',
        items: [
          { name: 'Protanomaly', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.', colors: this.protanomalyColors },
          { name: 'Protanopia', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.', colors: this.protanopiaColors },
        ],
      },
      {
        group: 'Deutan (green)',
        items: [
          { name: 'Deuteranomaly', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.', colors: this.deuteranomalyColors },
          { name: 'Deuteranopia', note: 'No functional M-cones. Red and green hues are confused at normal luminance.', colors: this.deuteranopiaColors },
        ],
      },
      {
        group: 'Tritan (blue)',
        items: [
          { name: 'Tritanomaly', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.', colors: this.tritanomalyColors },
          { name: 'Tritanopia', note: 'No functional S-cones. Blue and yellow hues are confused.', colors: this.tritanopiaColors },
        ],
      },
      {
        group: 'Monochromacy (no color)',
        items: [
          { name: 'Achromatomaly', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.', colors: this.achromatomalyColors },
          { name: 'Achromatopsia', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.', colors: this.achromatopsiaColors },
        ],
      },
    ];
  }

  /**
   * Build the hero's banded backdrop from the current palette colors,
   * boomeranged for a seamless repeat - the same treatment as the color page.
   * Applied (via the `striped-bg` class) only when no theme/image backs the
   * hero; the colors come straight from the palette ("the theme's colors").
   */
  private buildHeroBackground(): void {
    const colors = this.pickHeroColors();
    if ( colors.length === 0 ) {
      this.heroBackground = '';
      return;
    }
    // Same banded backdrop style as the color tool: a single hard-edged ramp
    // spanning the width (percent bands), boomeranged so it returns smoothly,
    // drawn at reduced opacity over the editor canvas.
    const angle = 115;
    const alpha = this.alphaHex( 0.6 );
    const ramp = colors.concat( colors.slice( 1, -1 ).reverse() );
    const band = 100 / ramp.length;
    const stops: string[] = [];
    ramp.forEach( ( color, i ) => {
      stops.push( `${ color }${ alpha } ${ i * band }%`, `${ color }${ alpha } ${ ( i + 1 ) * band }%` );
    } );
    this.heroBackground = `linear-gradient(${ angle }deg, ${ stops.join( ', ' ) }), var(--background-color)`;
  }

  /** Valid #RRGGBB colors from the current palette, in order. */
  private pickHeroColors(): string[] {
    const out: string[] = [];
    for ( const item of this.palette || [] ) {
      const raw = item?.value;
      if ( typeof raw !== 'string' ) {
        continue;
      }
      const hex = raw.startsWith( '#' ) ? raw : '#' + raw;
      if ( /^#[0-9a-fA-F]{6}$/.test( hex ) ) {
        out.push( hex );
      }
    }
    return out;
  }

  /** Two-digit hex alpha (00-ff) for an opacity ratio in [0, 1]. */
  private alphaHex( ratio: number ): string {
    const value = Math.max( 0, Math.min( 255, Math.round( ratio * 255 ) ) );
    return value.toString( 16 ).padStart( 2, '0' );
  }

  addNewColor( index: number, value: string ) {
    if ( this.palette.length >= 10 ) {
      return;
    }
    this.palette.splice( index + 1, 0, { value } );

    this.uploadedImg = undefined;
    this.initializer = undefined;

    const params = this.palette.map( item => item.value.replace( '#', '' ) ).join( '-' );
    this.router.navigate( [ URLS.tools.palette + '/' + params ], { state: { scrollToTop: false } } );
  }

  removeColor( index: number ) {
    if ( this.palette.length < 4 ) {
      return;
    }
    this.palette.splice( index, 1 );

    this.uploadedImg = undefined;
    this.initializer = undefined;

    const params = this.palette.map( item => item.value.replace( '#', '' ) ).join( '-' );
    this.router.navigate( [ URLS.tools.palette + '/' + params ], { state: { scrollToTop: false } } );
  }

  // The generator the current palette came from, so the Initialize dropdown can
  // reflect it on landing (image / theme / a harmony scheme); '' for a custom
  // palette with no recognised source.
  get currentGenerator(): string {
    if ( this.uploadedImg ) { return 'image'; }
    if ( this.foundTheme ) { return 'theme'; }
    return this.scheme || '';
  }

  // Hero subtitle: which variant to render (the line always reserves its space
  // even when there is nothing to say).
  get subtitleKind(): string {
    if ( this.uploadedImg ) { return 'image'; }
    if ( this.foundTheme ) { return 'theme'; }
    const sourced = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool' ];
    if ( sourced.includes( this.scheme ) && this.initializer ) { return 'harmony'; }
    if ( this.scheme === 'full' ) { return 'full'; }
    // Anything else with colours present = a custom (user-edited / bare-hex) theme.
    return this.palette.length ? 'custom' : '';
  }

  // The theme name with a single capitalised "Theme" suffix (no duplication).
  get themeName(): string {
    const name = ( this.foundTheme?.name || '' ).trim().replace( /\s*theme\s*$/i, '' );
    return `${ name } Theme`;
  }

  // Title-cased name of the colour the palette was seeded from.
  get initializerName(): string {
    return ( this.initializer?.colorName || '' ).replace( /\b\w/g, ( c: string ) => c.toUpperCase() );
  }

  private themeHref( theme: any ): string {
    for ( const line of THEMES ) {
      if ( line.products.some( ( p: any ) => p.key === theme?.key ) ) {
        return `${ URLS.theme }?product=${ theme.key }&category=${ ( line.name || '' ).toLowerCase() }`;
      }
    }
    return '';
  }

  private colorHref( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return h ? `${ URLS.tools.color }/${ h }` : '';
  }

  /** over every section card, the generator and the source (owner 2026-10-12): "Analogous · Istanbul Theme",
   *  "Codigrate Themes · Tallinn Theme", "Full Random · seed-..." (a middle dot, the site uses no dashes) */
  get sourceLabel(): string {
    const s = this.subtitle;
    switch ( s.kind ) {
      case 'theme': return 'Codigrate Themes · ' + ( s.themeName || 'Theme' );
      case 'harmony': return this.schemeTitle + ' · ' + ( s.name || '' );
      case 'image': return 'From Image · ' + ( s.imageName || 'Uploaded Image' );
      case 'full': return 'Full Random · ' + ( s.seed ? 'seed-' + s.seed : 'Random' );
      default: return 'Custom · ' + ( s.seed ? 'seed-' + s.seed : 'Palette' );
    }
  }

  // the scheme in Title Case for the hero title ("Split Complementary Palette", owner 2026-10-12)
  get schemeTitle(): string {
    return ( this.subtitle.scheme || '' ).trim().replace( /\b\w/g, ch => ch.toUpperCase() );
  }

  // Structured subtitle the widget renders.
  get subtitle(): PaletteSubtitle {
    return {
      kind: this.subtitleKind as PaletteSubtitle[ 'kind' ],
      themeName: this.themeName,
      logo: this.foundTheme?.logo,
      imageSrc: this.uploadedImg?.src,
      imageName: this.uploadedImg?.id,
      scheme: this.scheme,
      name: this.initializerName,
      color: this.initializer?.value,
      seed: this.subtitleKind === 'custom'
        ? this.paletteGen.paletteSeed( this.palette.map( p => p.value ) )
        : ( this.fullSeed || '' ),
      themeHref: this.foundTheme ? this.themeHref( this.foundTheme ) : '',
      colorHref: this.initializer?.value ? this.colorHref( this.initializer.value ) : '',
    };
  }

  // Reflect the produced palette + its source in the document head, so each
  // generated / shared palette carries a descriptive, indexable title +
  // description instead of the static page one. Driven off the same subtitle
  // state shown in the widget header (theme / harmony seed / random seed / image
  // / custom).
  private refreshSeo(): void {
    // Blank landings render at the bare /tools/palette URL (skipLocationChange);
    // its static landing meta must survive, so only real param URLs get the
    // per-palette head.
    if ( !this.location.path().split( '?' )[ 0 ].startsWith( URLS.tools.palette + '/' ) ) {
      return;
    }
    const sub = this.subtitle;
    let title = 'Color Palette Generator - Free Hex Palette Maker & Color Codes | Codigrate';
    let lead = 'Free color palette generator by Codigrate.';
    const extraKeyword: string[] = [];
    switch ( sub.kind ) {
      case 'theme':
        title = `${ sub.themeName } Color Palette | Codigrate`;
        lead = `The color palette of the ${ sub.themeName }.`;
        extraKeyword.push( ( sub.themeName || '' ).toLowerCase() );
        break;
      case 'harmony':
        title = sub.name
          ? `${ this.cap( sub.scheme || '' ) } Color Palette from ${ sub.name } | Codigrate`
          : `${ this.cap( sub.scheme || '' ) } Color Palette | Codigrate`;
        lead = `Generated a ${ sub.scheme } palette${ sub.name ? ' from ' + sub.name : '' }.`;
        extraKeyword.push( `${ sub.scheme } palette`, ...( sub.name ? [ sub.name.toLowerCase() ] : [] ) );
        break;
      case 'full':
        title = sub.seed ? `Random Color Palette from seed-${ sub.seed } | Codigrate` : 'Random Color Palette | Codigrate';
        lead = `Generated a random color palette${ sub.seed ? ' from seed-' + sub.seed : '' }.`;
        extraKeyword.push( 'random color palette', 'random palette generator' );
        break;
      case 'image':
        title = 'Color Palette from an Image | Codigrate';
        lead = 'A color palette extracted from the colors of an uploaded image.';
        extraKeyword.push( 'palette from image', 'image color extractor' );
        break;
      case 'custom':
        title = sub.seed ? `Custom Color Palette from seed-${ sub.seed } | Codigrate` : 'Custom Color Palette | Codigrate';
        lead = `A custom color palette${ sub.seed ? ' from seed-' + sub.seed : '' }.`;
        extraKeyword.push( 'custom color palette' );
        break;
    }
    // Keep the whole description within Bing's 160-char cap: the variable
    // `lead` (up to ~63 chars for the harmony/image kinds) plus this ~84-char
    // tail stays under 160 for every palette kind, where the old tail overran
    // to ~180+.
    const description = `${ lead } Copy the hex codes, convert to RGB, HSL and CMYK, and check harmony and readability.`;
    this.seoService.setMetaData( {
      title,
      description,
      keywords: [ 'color palette generator', 'palette generator', 'hex color palette', 'color scheme', ...extraKeyword, 'codigrate' ].filter( Boolean ),
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  private cap( s: string ): string {
    return ( s || '' ).replace( /\b\w/g, ( c: string ) => c.toUpperCase() );
  }

  // The hero backdrop (--hero-bg): uploaded image, else the source theme image,
  // else the palette-derived gradient.
  get heroBgValue(): string {
    if ( this.uploadedImg?.src ) {
      return `url(${ this.uploadedImg.src }) center / cover`;
    }
    if ( this.foundTheme?.backgroundImages?.length ) {
      return `url(${ this.foundTheme.backgroundImages[ 0 ].src }) center / cover`;
    }
    return this.heroBackground;
  }

  // Widget edited a colour (picker or hex): apply and sync the URL.
  onEditColor( change: { index: number; value: string } ): void {
    const item = this.palette[ change.index ];
    if ( !item ) {
      return;
    }
    item.value = change.value;
    this.becomeCustom();
    this.onColorChange( change.value );
  }

  // an edited palette is the user's own: drop the source it came from, so the hero reads Custom Theme
  // (owner 2026-10-12: the title kept "Source: Indian Mesa" after the colours changed)
  private becomeCustom(): void {
    this.uploadedImg = undefined;
    this.foundTheme = undefined;
    this.initializer = undefined;
    this.scheme = '';
    this.fullSeed = '';
  }

  // Widget asked for a generated palette (an option from the Initialize menu).
  onGenerate( scheme: string ): void {
    this.uploadedImg = undefined;
    this.initializer = undefined;

    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = any theme
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.foundTheme?.key );
      if ( !t.hexes.length ) { return; }
      const hexes = t.hexes.slice( 0, this.count );
      this.palette = hexes.map( h => ( { value: h } ) );
      const themeParams = hexes.map( h => h.replace( '#', '' ) ).join( '-' );
      this.router.navigate( [ URLS.tools.palette + '/' + themeParams ], { queryParams: { theme: t.theme?.key }, state: { scrollToTop: false } } );
      return;
    }

    const result = this.paletteGen.build( scheme, this.count );
    if ( !result.hexes.length ) {
      return;
    }
    this.palette = result.hexes.map( h => ( { value: h } ) );
    const params = result.hexes.map( h => h.replace( '#', '' ) ).join( '-' );
    const queryParams: { scheme: string; initializer?: string; seed?: string } = { scheme: result.scheme };
    if ( result.initializer ) {
      queryParams.initializer = result.initializer;
    }
    if ( result.seed ) {
      queryParams.seed = result.seed;
    }
    this.router.navigate( [ URLS.tools.palette + '/' + params ], { queryParams, state: { scrollToTop: false } } );
  }

  // The "+" tile: append a fresh random swatch (then it can be edited/dragged).
  addColor(): void {
    this.becomeCustom();
    this.addNewColor( this.palette.length - 1, this.colorService.generateRandomHexColor() );
  }

  setColorBlindnessSubType( colorBlindnessSubType: string ) {
    this.selectedColorBlindnessSubType = colorBlindnessSubType;
    this.setSelectedColorBlindnessColors();
  }

  private setSelectedColorBlindnessColors() {
    switch ( this.selectedColorBlindnessSubType ) {
      case 'protanomaly' :
      default:
        this.selectedColorBlindnessObject = {
          title: 'Protanomaly',
          explanation: 'Protanomaly is a type of red-weakness where the red photoreceptors in the eyes are less sensitive.\n' +
            '          People with protanomaly experience reds, oranges, and yellows as appearing greener and less bright.\n' +
            '          Everyday tasks that involve distinguishing between these colors can be more challenging, especially\n' +
            '          in low-light conditions. Protanomaly is the most common type of red-green color blindness.',
          colors: this.protanomalyColors
        };
        break;
      case 'deuteranomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Deuteranomaly',
          explanation: 'Deuteranomaly is a type of green-weakness where the green photoreceptors are less sensitive.\n' +
            '          This condition causes greens, oranges, and reds to appear shifted towards red. As a result,\n' +
            '          it can be difficult to differentiate between these colors. Deuteranomaly is the most common form\n' +
            '          of color blindness, affecting up to 5% of males but is rare in females.',
          colors: this.deuteranomalyColors
        };
        break;
      case 'tritanomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Tritanomaly',
          explanation: 'Tritanomaly is a type of blue-weakness where blue photoreceptors are less sensitive.\n' +
            '          This condition makes blues appear greener, and it can be hard to distinguish yellow and red\n' +
            '          from pink. Tritanomaly is quite rare compared to other types of color blindness and can affect\n' +
            '          the ability to perform tasks that rely on color differentiation, such as reading colored maps or graphs.',
          colors: this.tritanomalyColors
        };
        break;
      case 'achromatomaly' :
        this.selectedColorBlindnessObject = {
          title: 'Achromatomaly',
          explanation: 'Achromatomaly is a type of incomplete color blindness where there are fewer functioning cones in the retina.\n' +
            '          People with achromatomaly have limited color vision and often see a washed-out palette of colors.\n' +
            '          This condition can make the world appear less vibrant and can affect one\'s ability to perform tasks\n' +
            '          that require accurate color perception, such as selecting matching clothing or interpreting colored charts.',
          colors: this.achromatomalyColors
        };
        break;
      case 'protanopia' :
        this.selectedColorBlindnessObject = {
          title: 'Protanopia',
          explanation: 'Protanopia is a type of red-blindness where red photoreceptors are completely absent.\n' +
            '          People with protanopia have difficulty distinguishing between red and green colors, with reds\n' +
            '          appearing much darker than they are to those with normal vision. This condition can significantly\n' +
            '          impact daily activities, such as reading traffic lights or selecting ripe fruits.',
          colors: this.protanopiaColors
        };
        break;
      case 'deuteranopia' :
        this.selectedColorBlindnessObject = {
          title: 'Deuteranopia',
          explanation: 'Deuteranopia is a type of green-blindness where green photoreceptors are completely absent.\n' +
            '          This condition results in a similar effect to protanopia, causing difficulties in distinguishing\n' +
            '          between red and green colors. However, the brightness of colors is not as affected as in protanopia.\n' +
            '          Everyday activities, such as gardening or cooking, can be more challenging due to color confusion.',
          colors: this.deuteranopiaColors
        };
        break;
      case 'tritanopia' :
        this.selectedColorBlindnessObject = {
          title: 'Tritanopia',
          explanation: 'Tritanopia is a type of blue-blindness where blue photoreceptors are completely absent.\n' +
            '          This condition leads to difficulty distinguishing between blue and yellow colors, with blues\n' +
            '          often appearing as greenish and yellows appearing as light gray. Tritanopia is extremely rare\n' +
            '          and can impact various aspects of life, including fashion, art, and any tasks involving color coding.',
          colors: this.tritanopiaColors
        };
        break;
      case 'achromatopsia' :
        this.selectedColorBlindnessObject = {
          title: 'Achromatopsia',
          explanation: 'Achromatopsia is a type of complete color blindness where all cone photoreceptors are absent.\n' +
            '          Individuals with achromatopsia see the world in shades of gray, as they rely entirely on rod\n' +
            '          photoreceptors, which detect light intensity but not color. This condition can greatly impact\n' +
            '          daily life, as it affects the ability to perceive color-coded information and can reduce visual\n' +
            '          acuity, especially in bright light.',
          colors: this.achromatopsiaColors
        };
        break;
    }
  }

  redirectToProductUrl() {
    this.productService.redirectToProductUrl( this.foundTheme );
  }

  private updatePath( removeFoundTheme = true ) {
    if ( removeFoundTheme === true ) {
      this.foundTheme = undefined;
    }

    // built from the route, never by dropping the last segment: on the bare landing
    // (/tools/palette) that dropped 'palette' itself and wrote /tools/<hexes>, a page that does
    // not exist, which is what Mixpanel then recorded as the current url (owner, 2026-09-23)
    const params = this.palette.map( item => item.value.replace( '#', '' ) ).join( '-' ).toLowerCase();
    const current = this.location.path().split( '?' )[ 0 ];
    const newPath = URLS.tools.palette + '/' + params;

    if ( current.toLowerCase() !== newPath ) {
      this.location.go( newPath );

      const paramMap = new Map<string, string>();
      paramMap.set( 'palette', params );
      this.seoService.seoAndAnalytics( this.location.path(), paramMap, false );
      // Re-apply the dynamic, source-aware head over the static route SEO that
      // seoAndAnalytics just set, so an edited palette keeps its descriptive title.
      this.refreshSeo();
    }
  }

  openRouteInNewTab( route: string ) {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  openImageInNewTab(): void {
    const imageUrl = this.uploadedImg?.src;
    if ( imageUrl ) {
      // Create a Blob from the Base64 data
      const base64Data = imageUrl.split( ',' )[ 1 ]; // Extract Base64 part
      const byteCharacters = atob( base64Data );
      const byteNumbers = new Array( byteCharacters.length );

      for ( let i = 0; i < byteCharacters.length; i++ ) {
        byteNumbers[ i ] = byteCharacters.charCodeAt( i );
      }

      const byteArray = new Uint8Array( byteNumbers );
      const blob = new Blob( [ byteArray ], { type: 'image/jpeg' } );

      // Create a URL for the Blob and open it
      const blobUrl = URL.createObjectURL( blob );
      window.open( blobUrl, '_blank' );

      // Optionally, revoke the object URL after usage
      setTimeout( () => URL.revokeObjectURL( blobUrl ), 100 );
    } else {
      console.error( 'Image source is invalid or not set' );
    }
  }
}
